import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { LucideExternalLink, LucideMonitor, LucideSmartphone } from "lucide-react";
import { Segmented } from "../ui/SettingsUI";

/*
  실제 쇼핑몰 미리보기 (2026-10-26).
  쇼핑몰 서버의 /shop/design-preview 를 iframe 으로 띄우고, 저장 전 설정을 postMessage 로 보낸다.
  쇼핑몰은 그 설정으로 진짜 메인 · 상품 주문 화면 템플릿을 다시 그린다(pod24_frontend_shop designPreview).
  쇼핑몰 주소를 모르거나 일정 시간 안에 응답이 없으면 MES 의 간이 미리보기(fallback)를 보여 준다.
*/

export type PreviewPage = "main" | "product";
export type PreviewDevice = "desktop" | "mobile";

export interface PreviewScrollRequest {
  target: "top" | "footer" | "section";
  sectionId?: number;
  /** 같은 대상으로 다시 스크롤할 때도 반응하게 하는 값 */
  nonce: number;
}

interface ShopLivePreviewProps {
  /** 쇼핑몰 주소 (예: https://dh.pod24.co.kr). 없으면 간이 미리보기 */
  shopBaseUrl: string | null;
  page: PreviewPage;
  onPageChange: (page: PreviewPage) => void;
  device: PreviewDevice;
  onDeviceChange: (device: PreviewDevice) => void;
  draft: unknown;
  /** 업로드 전 띠배너 이미지(data URL). 쇼핑몰 서버로 보내지 않고 미리보기 화면에서 바꿔 넣는다 */
  stripBannerDataUrl: string | null;
  scrollRequest: PreviewScrollRequest | null;
  fallback: React.ReactNode;
}

const DESKTOP_WIDTH = 1280;
const MOBILE_WIDTH = 390;
const READY_TIMEOUT_MS = 10000;
const POST_DEBOUNCE_MS = 300;

type Status = "loading" | "rendering" | "ready" | "error" | "unavailable";

export const ShopLivePreview = ({
  shopBaseUrl,
  page,
  onPageChange,
  device,
  onDeviceChange,
  draft,
  stripBannerDataUrl,
  scrollRequest,
  fallback,
}: ShopLivePreviewProps) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const viewportRef = useRef<HTMLDivElement>(null);
  const [status, setStatus] = useState<Status>(shopBaseUrl ? "loading" : "unavailable");
  const [isReady, setIsReady] = useState(false);
  const isReadyRef = useRef(false);
  const [viewport, setViewport] = useState({ width: 0, height: 0 });

  const shopOrigin = (() => {
    try {
      return shopBaseUrl ? new URL(shopBaseUrl).origin : null;
    } catch {
      return null;
    }
  })();

  // 쇼핑몰 미리보기 화면이 보내는 신호 (준비 · 그리기 완료 · 실패)
  useEffect(() => {
    if (!shopOrigin) {
      setStatus("unavailable");
      return;
    }
    setStatus("loading");
    setIsReady(false);
    isReadyRef.current = false;
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== shopOrigin || event.source !== iframeRef.current?.contentWindow) return;
      const type = (event.data as { type?: string } | null)?.type;
      if (type === "POD24_PREVIEW_READY") {
        isReadyRef.current = true;
        setIsReady(true);
      }
      else if (type === "POD24_PREVIEW_RENDERED") setStatus("ready");
      else if (type === "POD24_PREVIEW_FAILED") setStatus("error");
    };
    window.addEventListener("message", onMessage);
    const timer = window.setTimeout(() => {
      if (!isReadyRef.current) setStatus("unavailable");
    }, READY_TIMEOUT_MS);
    return () => {
      window.removeEventListener("message", onMessage);
      window.clearTimeout(timer);
    };
  }, [shopOrigin]);

  // 설정이 바뀌면 잠시 모았다가 보낸다 (글자 입력마다 쇼핑몰을 다시 그리지 않게)
  useEffect(() => {
    if (!isReady || !shopOrigin) return;
    const timer = window.setTimeout(() => {
      setStatus((current) => (current === "unavailable" ? current : "rendering"));
      iframeRef.current?.contentWindow?.postMessage({ type: "POD24_PREVIEW_DRAFT", page, draft, stripBannerDataUrl }, shopOrigin);
    }, POST_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [isReady, shopOrigin, page, draft, stripBannerDataUrl]);

  useEffect(() => {
    if (!isReady || !shopOrigin || !scrollRequest) return;
    iframeRef.current?.contentWindow?.postMessage(
      { type: "POD24_PREVIEW_SCROLL", target: scrollRequest.target, sectionId: scrollRequest.sectionId },
      shopOrigin,
    );
  }, [isReady, shopOrigin, scrollRequest]);

  // 미리보기 영역 크기에 맞춰 PC 화면(1280px)을 줄여 보여 준다
  useLayoutEffect(() => {
    const element = viewportRef.current;
    if (!element) return;
    const update = () => setViewport({ width: element.clientWidth, height: element.clientHeight });
    update();
    const observer = new ResizeObserver(update);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const frameWidth = device === "desktop" ? DESKTOP_WIDTH : MOBILE_WIDTH;
  const scale = viewport.width > 0 ? Math.min(1, (viewport.width - (device === "mobile" ? 32 : 0)) / frameWidth) : 1;
  const frameHeight = device === "desktop" ? viewport.height / scale : Math.min(844, viewport.height - 32) / scale;

  const statusText: Record<Status, string> = {
    loading: "쇼핑몰 불러오는 중",
    rendering: "반영하는 중",
    // 반영이 끝난 상태는 따로 알리지 않는다 (불러오는 중 · 실패 · 간이 미리보기만 표시)
    ready: "",
    error: "미리보기를 그리지 못했습니다",
    unavailable: "간이 미리보기",
  };

  const showFallback = status === "unavailable";

  return (
    <div className="flex flex-col h-full min-h-0 bg-white rounded-[10px] border border-[#E5E7EB] overflow-hidden">
      <div className="flex flex-row flex-wrap items-center gap-[0.5rem] px-[0.75rem] py-[0.5rem] border-b border-[#EEEEEE]">
        <div className="w-[11rem]">
          <Segmented
            ariaLabel="미리볼 화면"
            value={page}
            options={[
              { value: "main", label: "메인" },
              { value: "product", label: "상품 주문" },
            ]}
            onChange={onPageChange}
          />
        </div>
        {!showFallback ? (
          <div className="w-[6.5rem]">
            <Segmented
              ariaLabel="화면 크기"
              value={device}
              options={[
                { value: "desktop", label: <LucideMonitor aria-label="PC" className="w-4 h-4 mx-auto" /> },
                { value: "mobile", label: <LucideSmartphone aria-label="모바일" className="w-4 h-4 mx-auto" /> },
              ]}
              onChange={onDeviceChange}
            />
          </div>
        ) : null}
        <span className={`ml-auto text-[12px] ${status === "error" ? "text-[#F24A4A]" : "text-[#888]"}`} role="status">
          {statusText[status]}
        </span>
        {shopBaseUrl ? (
          <a
            href={`${shopBaseUrl}/shop/`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex flex-row items-center gap-[0.375rem] h-[2.25rem] px-[0.875rem] rounded-[8px] border border-[#E0E0E0] bg-white
              text-[13px] font-medium text-[#252525] hover:bg-[#F5F8FF] hover:border-[#4285F4] hover:text-[#4285F4] transition-colors"
          >
            <LucideExternalLink className="w-4 h-4" />
            쇼핑몰 열기
          </a>
        ) : null}
      </div>

      <div ref={viewportRef} className={`relative flex-1 min-h-[480px] overflow-hidden ${device === "mobile" && !showFallback ? "bg-[#F3F4F6]" : ""}`}>
        {showFallback ? (
          <div className="absolute inset-0 overflow-y-auto">{fallback}</div>
        ) : (
          <div
            className={device === "mobile" ? "absolute left-1/2 top-[16px]" : "absolute left-0 top-0"}
            style={{
              width: frameWidth,
              height: frameHeight,
              transform: `${device === "mobile" ? "translateX(-50%) " : ""}scale(${scale})`,
              transformOrigin: device === "mobile" ? "top center" : "top left",
            }}
          >
            {shopBaseUrl ? (
              <iframe
                ref={iframeRef}
                title="쇼핑몰 미리보기"
                src={`${shopBaseUrl}/shop/design-preview`}
                className={`w-full h-full border-0 bg-white ${device === "mobile" ? "rounded-[16px] shadow-[0_4px_24px_rgba(0,0,0,0.12)]" : ""}`}
              />
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
};
